import React from "react";
import { ArrowUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { poloAge } from "@/lib/polo-age";

/**
 * Small badge that renders a horse's age using the polo "rising"
 * convention. On or after 1 Jan 2027 (see polo-age.js), horses aged
 * 2+ years get an upward-arrow indicator next to the numeric age and
 * a "Rising N" tooltip explaining what it means.
 *
 * Modes:
 *   variant="badge"  (default) — "5y ↑" with the arrow as an inline
 *                                icon and a tooltip revealing the
 *                                Rising label. Best for table cells
 *                                and card headers.
 *   variant="text"             — "Rising 5" only, arrow prefix, no
 *                                base age. Best for prose ("known as
 *                                Rising 5") or dashboards where the
 *                                emphasis is on the rising label.
 *
 * `age` accepts number | string | null. Nullish returns "".
 */
export function HorseAgeBadge({ age, variant = "badge", className = "" }) {
  const p = poloAge(age);
  if (!p.baseLabel) return null;

  // Pre-rising-epoch — behave exactly like a normal text node so the
  // component is a drop-in replacement anywhere age is shown.
  if (!p.isRising) {
    return (
      <span className={`tabular-nums ${className}`} data-testid="horse-age-plain">
        {p.baseLabel}
      </span>
    );
  }

  if (variant === "text") {
    // Prose usage: "The horse is known as Rising 5." We render the arrow
    // as an accessible visual cue but the label itself is the payload.
    return (
      <span
        className={`inline-flex items-center gap-1 font-medium ${className}`}
        data-testid="horse-age-rising-text"
        title={`Polo convention: ${p.risingLabel}. Horse's stored age is ${p.baseLabel}.`}
      >
        <ArrowUp className="h-3 w-3 text-[hsl(var(--ap-brass))]" aria-hidden />
        {p.risingLabel}
      </span>
    );
  }

  // Default: base age with an inline ↑ + tooltip. Tabular-nums keeps
  // columns aligned across rows that do/don't have the arrow.
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 tabular-nums cursor-help ${className}`}
            data-testid="horse-age-rising-badge"
            aria-label={`${p.baseLabel}, ${p.risingLabel}`}
          >
            {p.baseLabel}
            <ArrowUp
              className="h-3 w-3 text-[hsl(var(--ap-brass))]"
              aria-hidden
              data-testid="horse-age-rising-arrow"
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px]">
          <div className="text-xs font-medium">{p.risingLabel}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Polo convention: horses "rise" on 1 January each year. This horse's stored age is {p.baseLabel};
            it will turn {p.risingAge} during this calendar year.
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
